/**
 * HeroAnimationCanvas - Background particle animation for the hero section
 *
 * Features:
 * - Atmosphere spheres with teal/azure glow
 * - Twinkling stars
 * - Responsive (mobile vs desktop particle counts)
 * - Respects prefers-reduced-motion
 * - Reads colors from CSS variables
 *
 * Extracted from HeroSectionImmersive.tsx
 */

import React, { useRef, useEffect } from 'react';

interface AtmosphereSphere {
  x: number;
  y: number;
  size: number;
  vx: number;
  vy: number;
  highlightOp: number;
  bodyOp: number;
  layer: string;
  depth: number;
  phase: number;
}

interface Star {
  x: number;
  y: number;
  size: number;
  speed: number;
  alpha: number;
  phase: number;
}

interface RGB {
  r: number;
  g: number;
  b: number;
}

export interface HeroAnimationCanvasProps {
  className?: string;
}

/**
 * Parse CSS color string to RGB object
 */
const parseCssColorToRgb = (raw: string): RGB | null => {
  const value = raw.trim();
  if (!value) return null;

  if (value.startsWith("#")) {
    const hex = value.slice(1);
    const normalized =
      hex.length === 3
        ? hex.split("").map((c) => c + c).join("")
        : hex.length >= 6
          ? hex.slice(0, 6)
          : null;
    if (!normalized) return null;
    const intVal = Number.parseInt(normalized, 16);
    if (!Number.isFinite(intVal)) return null;
    return {
      r: (intVal >> 16) & 255,
      g: (intVal >> 8) & 255,
      b: intVal & 255,
    };
  }

  const rgbMatch = value.match(/rgba?\(([^)]+)\)/i);
  if (rgbMatch) {
    const [r, g, b] = rgbMatch[1]
      .split(",")
      .slice(0, 3)
      .map((v) => Number.parseFloat(v.trim()));
    if ([r, g, b].every((v) => Number.isFinite(v))) {
      return { r, g, b };
    }
  }
  return null;
};

/**
 * Read RGB from CSS variable with fallback
 */
const readRgb = (cssVar: string, fallback: RGB): RGB => {
  if (typeof window === "undefined") return fallback;
  const styles = getComputedStyle(document.documentElement);
  const raw = styles.getPropertyValue(cssVar);
  return parseCssColorToRgb(raw) ?? fallback;
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

export const HeroAnimationCanvas: React.FC<HeroAnimationCanvasProps> = ({
  className = "hero-canvas",
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let width = 0;
    let height = 0;
    let dpr = 1;
    let atmosphereSpheres: AtmosphereSphere[] = [];
    let stars: Star[] = [];
    let animationId: number;
    let lastTime = performance.now();
    let motionScale = 1;

    // Read theme colors
    const MIDASH_TEAL = readRgb("--color-accent-light", { r: 45, g: 212, b: 191 });
    const MIDASH_AZURE = readRgb("--color-info", { r: 120, g: 190, b: 255 });
    const STAR_WHITE = readRgb("--color-text", { r: 255, g: 255, b: 255 });

    const createSphere = (layer: string): AtmosphereSphere => {
      const isMobile = width < 768;
      const prefersReducedMotion =
        typeof window !== "undefined" &&
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const scale = prefersReducedMotion ? 0 : isMobile ? 0.35 : 1;
      const depth = layer === "bg" ? Math.random() * 0.5 + 0.2 : Math.random() * 0.4 + 0.6;
      const baseSize = layer === "bg" ? Math.random() * 30 + 10 : Math.random() * 60 + 20;
      const speedScale = 0.35 + depth * 0.65;
      const baseSpeed = layer === "bg" ? 18 : 26; // px/s before scaling
      return {
        x: Math.random() * width,
        y: Math.random() * height,
        size: baseSize * (0.85 + depth * 0.35),
        vx: (Math.random() - 0.5) * baseSpeed * speedScale * scale,
        vy: (Math.random() - 0.5) * baseSpeed * speedScale * scale,
        highlightOp: (layer === "bg" ? 0.08 : 0.15) * (0.75 + depth * 0.6),
        bodyOp: (layer === "bg" ? 0.03 : 0.06) * (0.75 + depth * 0.6),
        layer,
        depth,
        phase: Math.random() * Math.PI * 2,
      };
    };

    const createStar = (): Star => ({
      x: Math.random() * width,
      y: Math.random() * height,
      size: Math.random() * 2,
      speed: Math.random() * 10 + 6, // px/s
      alpha: Math.random() * 0.3 + 0.1,
      phase: Math.random() * Math.PI * 2,
    });

    const init = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      dpr = clamp(window.devicePixelRatio || 1, 1, 2);
      lastTime = performance.now();

      const isMobile = width < 768;
      const prefersReducedMotion =
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      motionScale = prefersReducedMotion ? 0 : isMobile ? 0.35 : 1;

      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      atmosphereSpheres = [];
      const bgCount = width < 768 ? 10 : 20;
      const fgCount = width < 768 ? 8 : 15;
      for (let i = 0; i < bgCount; i++) atmosphereSpheres.push(createSphere("bg"));
      for (let i = 0; i < fgCount; i++) atmosphereSpheres.push(createSphere("fg"));
      stars = [];
      const starCount = width < 768 ? 90 : 200;
      for (let i = 0; i < starCount; i++) stars.push(createStar());
    };

    const loop = () => {
      const now = performance.now();
      const dt = clamp((now - lastTime) / 1000, 0, 0.05);
      lastTime = now;
      const t = now * 0.001;
      ctx.clearRect(0, 0, width, height);

      // Draw spheres (cinematic: teal-tinted, subtle glow, screen blend)
      ctx.globalCompositeOperation = "screen";
      atmosphereSpheres.forEach((s) => {
        s.x += s.vx * dt;
        s.y += s.vy * dt;
        // Wrap instead of bouncing so it doesn't feel like spheres are trapped in a box
        const pad = 160;
        if (s.x < -pad) s.x = width + pad;
        if (s.x > width + pad) s.x = -pad;
        if (s.y < -pad) s.y = height + pad;
        if (s.y > height + pad) s.y = -pad;

        const r = s.size;
        const pulse = 0.85 + 0.15 * Math.sin(t * 0.6 + s.phase);
        const highlightOp = s.highlightOp * pulse;
        const bodyOp = s.bodyOp * pulse;
        const color = s.layer === "bg" ? MIDASH_AZURE : MIDASH_TEAL;

        // Outer glow
        ctx.fillStyle = `rgba(${color.r}, ${color.g}, ${color.b}, ${bodyOp * 0.25})`;
        ctx.beginPath();
        ctx.arc(s.x, s.y, r * 1.35, 0, Math.PI * 2);
        ctx.fill();

        // Main body gradient
        const grad = ctx.createRadialGradient(
          s.x - r * 0.35,
          s.y - r * 0.35,
          0,
          s.x,
          s.y,
          r
        );
        grad.addColorStop(0, `rgba(${STAR_WHITE.r}, ${STAR_WHITE.g}, ${STAR_WHITE.b}, ${highlightOp})`);
        grad.addColorStop(0.55, `rgba(${color.r}, ${color.g}, ${color.b}, ${bodyOp})`);
        grad.addColorStop(1, "transparent");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(s.x, s.y, r, 0, Math.PI * 2);
        ctx.fill();
      });

      // Draw stars (subtle twinkle)
      ctx.globalCompositeOperation = "source-over";
      stars.forEach((s) => {
        s.x += s.speed * dt * motionScale;
        if (s.x > width + 2) s.x = -2;
        const twinkle = 0.7 + 0.3 * Math.sin(t * 1.2 + s.phase);
        ctx.fillStyle = `rgba(${STAR_WHITE.r}, ${STAR_WHITE.g}, ${STAR_WHITE.b}, ${s.alpha * twinkle})`;
        ctx.fillRect(s.x, s.y, s.size, s.size);
      });

      animationId = requestAnimationFrame(loop);
    };

    init();
    loop();

    const handleResize = () => init();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      aria-hidden="true"
    />
  );
};

export default HeroAnimationCanvas;
